import type { Competitor } from '../types'

// Sources (checked 2026-10-01): milkshake.app/pricing (USD, billed through the
// App Store and Google Play; prices vary by country), help.milkshake.app
// articles on platforms, looks, customization, fees, insights, custom domains,
// and the App Store listing.
const milkshake: Competitor = {
  slug: 'milkshake',
  name: 'Milkshake',
  tagline: 'Phone-first website builder for Instagram, edited in its mobile app.',
  site: 'https://milkshake.app',
  pricingUrl: 'https://milkshake.app/pricing/',
  checkedOn: '2026-10-01',
  seo: {
    title: 'Milkshake alternative: mychar.gg vs Milkshake compared (2026)',
    description: 'A Milkshake alternative you can edit on desktop or phone. Compare design, music, video clips, integrations, custom domains, and one-time pricing.'
  },
  hero: {
    title: 'The Milkshake alternative you can edit on any screen',
    description: 'Build your page in a live drag-and-drop editor in any browser, on desktop or phone, with effects, music, and clips, and pay once if you upgrade.'
  },
  summary: 'mychar.gg is a Milkshake alternative for anyone who wants to edit on a computer as well as a phone and wants more than cards: you design in a live editor in any browser, with animated effects, custom cursors, a music player, video clips, and YouTube, Twitch, and Discord sections, and Pro is €6.99 once. Milkshake is quicker to set up from your phone, and its free plan includes selling and 30 days of insights.',
  chooseMychar: [
    'want to edit on a computer, not only in a phone app',
    'want animated effects, custom cursors, and a music player',
    'want Twitch, YouTube, and Discord sections that update themselves',
    'want a custom domain without a $99.99 a year plan'
  ],
  chooseThem: [
    'want to build everything from your phone in a few minutes',
    'like the swipeable, card-by-card mini website format',
    'want to sell digital items from the free plan'
  ],
  features: {
    free_plan: { yes: true, text: 'Unlimited links, Milkshake branding' },
    paid_plans: 'Lite $2.99, Pro $6.99 a month, Pro+ $99.99 a year',
    billing: 'In-app subscription',
    first_year: '$59.99 (Pro, billed yearly)',
    custom_domain: { yes: true, text: 'Pro+, $99.99 a year' },
    remove_branding: { yes: true, text: 'Lite, $2.99 a month' },
    editor: 'Phone app only, card by card',
    themes: { yes: true, text: '15 looks per card' },
    fonts: 'Font picker',
    links: { yes: true, text: 'Unlimited' },
    video_clips: { yes: false, text: 'YouTube card' },
    youtube: 'YouTube video card',
    qr_code: true,
    analytics_free: { yes: true, text: '30 days, with sources and locations' },
    analytics_history: '30 days, 365 on Pro',
    store: { yes: true, text: 'Digital items, 12% fee on Free' },
    email_capture: { yes: true, text: 'Pro' },
    mobile_app: { yes: true, text: 'The only way to edit' }
  },
  plans: [
    { name: 'Free', price: '$0', period: 'forever', note: 'Unlimited links and cards, QR code, 30 days of insights, selling with a 12% fee.' },
    { name: 'Lite', price: '$2.99', period: '/month', note: 'Removes Milkshake branding.' },
    { name: 'Pro', price: '$5', period: '/month, billed yearly', note: 'Or $6.99 monthly. A year of insights, contact form, mailing list, SEO, 9% sales fee.' },
    { name: 'Pro+', price: '$8.33', period: '/month, billed yearly', note: 'Or $29.99 per quarter. Custom domain, Google Analytics, 7% sales fee.' }
  ],
  plansNote: 'USD, billed through the App Store or Google Play, so prices can vary by country. No free trial.',
  advantages: [
    {
      title: 'Edit on any screen',
      description: 'The editor runs in any modern browser, on desktop or phone, and it is your actual page. Milkshake edits only in its mobile app.',
      icon: 'i-lucide-monitor-smartphone'
    },
    {
      title: 'Animated effects and cursors',
      description: 'Rain, snow, fire, sakura, plasma, and more, plus custom cursors and cursor trails.',
      icon: 'i-lucide-sparkles'
    },
    {
      title: 'A music player and real clips',
      description: 'Upload tracks that play while people browse and video clips that play in place.',
      icon: 'i-lucide-music'
    },
    {
      title: 'YouTube, Twitch, and Discord',
      description: 'Your latest uploads, your Twitch live status and schedule, and your Discord profile, kept current on their own.',
      icon: 'i-lucide-radio'
    },
    {
      title: 'A cheaper custom domain',
      description: 'Connect your domain for €2.99 per month on any plan. Milkshake needs Pro+ at $99.99 a year.',
      icon: 'i-lucide-globe'
    },
    {
      title: 'One-time upgrades',
      description: 'Pro is €6.99 once and Elite €12.99 once. No in-app subscription.',
      icon: 'i-lucide-badge-euro'
    }
  ],
  strengths: [
    {
      title: 'Fastest on a phone',
      description: 'Milkshake is built for building on your phone, and its iOS app is rated 4.9 from about 15,000 ratings.'
    },
    {
      title: 'A mini website format',
      description: 'Swipeable cards with ready-made looks turn your bio link into something that feels like a small site.'
    },
    {
      title: 'A useful free plan',
      description: 'Free includes unlimited links and cards, 30 days of insights with sources and locations, and selling digital items.'
    },
    {
      title: 'Cheap branding removal',
      description: 'Lite removes Milkshake branding for $2.99 a month.'
    }
  ],
  sections: [
    {
      title: 'Why people look for a Milkshake alternative',
      paragraphs: [
        'Milkshake made bio pages feel like tiny websites you build from your phone, and it is quick at that. The most common frustration is the flip side: there is no web editor, so every change happens in the app, on a phone screen. Creators who plan their page on a computer, or want finer control over the design, run into that limit.',
        'The format is also fixed: cards with a set of looks for each. There are no animated backgrounds or custom cursors, video comes from YouTube, and a custom domain needs the Pro+ plan at $99.99 a year.'
      ]
    },
    {
      title: 'How mychar.gg is different',
      paragraphs: [
        'mychar.gg runs in the browser on any device, and the [editor](/docs/customization) is your page itself: edit text in place, drag sections, links, and widgets into position, and pick layouts, fonts, colors, backgrounds, effects, and cursors while it updates live. Your content plays on the page through the music player and video clips, and YouTube, Twitch, and Discord sections keep themselves current.',
        'Upgrades are one-time: Pro €6.99, Elite €12.99, and an optional €2.99 per month for your own domain. Milkshake is the better choice if you sell items from your page or simply prefer building everything in a phone app. The [guide for Instagram](/for/instagram) covers putting your link on your profile.'
      ]
    }
  ],
  switchSteps: [
    { title: 'Claim your username', description: 'Sign up free at [mychar.gg/signup](/signup), on your phone or computer.' },
    { title: 'Move your links', description: 'Add your platforms from the built-in list, unlimited on every plan. Links to any other URL start on Pro.' },
    { title: 'Turn cards into sections', description: 'Your Milkshake cards become sections: links, clips, a music player, games, a setup section, and more. Drag them into the order you want.' },
    { title: 'Design the page', description: 'Pick a layout, fonts, colors, and a background, or start from a community [skin](/skins).' },
    { title: 'Swap the link', description: 'Replace your msha.ke link in your Instagram and TikTok bios.' }
  ],
  faq: [
    {
      label: 'Can I edit mychar.gg on my phone?',
      content: 'Yes. The editor works in any modern mobile browser, and the same page is editable on a computer. There is no separate app to install.'
    },
    {
      label: 'Is mychar.gg cheaper than Milkshake?',
      content: 'Over a year, usually. mychar.gg Pro is €6.99 once. Milkshake Pro is $59.99 a year or $6.99 a month, and Lite is $2.99 a month just to remove branding.'
    },
    {
      label: 'Can I sell on mychar.gg like with the Milkshake Sell card?',
      content: 'No. mychar.gg has no checkout. You can link to a store as a custom link on Pro and Elite.'
    },
    {
      label: 'Can I use my own domain?',
      content: 'Yes. The custom domain add-on is €2.99 per month on any plan, including Free. See the [custom domain guide](/docs/profile/custom-domain).'
    }
  ]
}

export default milkshake
